import { useRef, useState, useEffect, useCallback } from "react";

/**
 * Draggable before/after comparison slider.
 * - Left side shows BEFORE
 * - Right side shows AFTER
 * - Drag handle reveals more / less of each side via clip-path.
 */
export default function BeforeAfterSlider({
    beforeUrl,
    afterUrl,
    title = "",
    location = "",
    testId = "before-after-slider",
}) {
    const containerRef = useRef(null);
    const [pct, setPct] = useState(50);
    const [dragging, setDragging] = useState(false);

    const updateFromClientX = useCallback((clientX) => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
        setPct((x / rect.width) * 100);
    }, []);

    useEffect(() => {
        if (!dragging) return;
        const onMove = (e) => {
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            updateFromClientX(clientX);
        };
        const onUp = () => setDragging(false);
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        window.addEventListener("touchmove", onMove, { passive: false });
        window.addEventListener("touchend", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onUp);
        };
    }, [dragging, updateFromClientX]);

    const onKeyDown = (e) => {
        if (e.key === "ArrowLeft") setPct((p) => Math.max(0, p - 4));
        if (e.key === "ArrowRight") setPct((p) => Math.min(100, p + 4));
    };

    return (
        <figure
            data-testid={testId}
            className="group relative w-full bg-[#0F0F0F] border border-white/10 select-none"
        >
            <div
                ref={containerRef}
                className="relative w-full overflow-hidden cursor-ew-resize aspect-[16/10]"
                onMouseDown={(e) => {
                    setDragging(true);
                    updateFromClientX(e.clientX);
                }}
                onTouchStart={(e) => {
                    setDragging(true);
                    updateFromClientX(e.touches[0].clientX);
                }}
            >
                {/* AFTER (base) */}
                <img
                    src={afterUrl}
                    alt={`${title} - after`}
                    className="absolute inset-0 w-full h-full object-cover"
                    draggable={false}
                />

                {/* BEFORE (clipped to left side) */}
                <img
                    src={beforeUrl}
                    alt={`${title} - before`}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
                    draggable={false}
                />

                {/* Labels */}
                <span className="absolute top-4 left-4 px-3 py-1 text-[10px] uppercase tracking-[0.25em] bg-black/70 text-white border border-white/20">
                    Before
                </span>
                <span className="absolute top-4 right-4 px-3 py-1 text-[10px] uppercase tracking-[0.25em] bg-[#F5A623] text-black">
                    After
                </span>

                {/* Divider line */}
                <div
                    className="absolute top-0 bottom-0 w-[2px] bg-white/90"
                    style={{ left: `calc(${pct}% - 1px)` }}
                />

                {/* Drag handle */}
                <button
                    type="button"
                    onKeyDown={onKeyDown}
                    aria-label="Drag to compare"
                    data-testid={`${testId}-handle`}
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 bg-[#F5A623] border-2 border-white text-black shadow-[0_4px_24px_rgba(0,0,0,0.5)] flex items-center justify-center hover:scale-105 transition-transform"
                    style={{ left: `${pct}%` }}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        setDragging(true);
                    }}
                    onTouchStart={(e) => {
                        e.stopPropagation();
                        setDragging(true);
                    }}
                >
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="square"
                    >
                        <polyline points="9 18 3 12 9 6" />
                        <polyline points="15 6 21 12 15 18" />
                    </svg>
                </button>
            </div>

        </figure>
    );
}
